import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { LayoutGrid, CheckCircle2, XCircle, Loader2 } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function PaymentSuccess() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState('checking');
  const [paymentData, setPaymentData] = useState(null);
  const sessionId = searchParams.get('session_id');

  useEffect(() => {
    if (sessionId) {
      pollPaymentStatus(sessionId, 0);
    } else {
      setStatus('error');
    }
  }, [sessionId]);

  const pollPaymentStatus = async (sid, attempts) => {
    if (attempts >= 5) {
      setStatus('timeout');
      return;
    }

    try {
      const { data } = await axios.get(`${BACKEND_URL}/api/payments/status/${sid}`, {
        withCredentials: true
      });
      setPaymentData(data);

      if (data.payment_status === 'paid') {
        setStatus('success');
        return;
      } else if (data.status === 'expired') {
        setStatus('expired');
        return;
      }

      setTimeout(() => pollPaymentStatus(sid, attempts + 1), 2000);
    } catch (error) {
      console.error('Error checking payment:', error);
      if (attempts < 4) {
        setTimeout(() => pollPaymentStatus(sid, attempts + 1), 2000);
      } else {
        setStatus('error');
      }
    }
  };

  return (
    <div className="min-h-screen bg-[#F8FAFC] flex flex-col">
      <header className="bg-white/80 backdrop-blur-xl border-b border-slate-200">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex items-center gap-3">
          <LayoutGrid size={32} className="text-[#0052FF]" />
          <h1 className="text-2xl font-medium tracking-tight text-slate-900">Hi Tech Industries</h1>
        </div>
      </header>

      <div className="flex-1 flex items-center justify-center p-8">
        <Card className="border border-slate-200 rounded-sm shadow-none max-w-lg w-full" data-testid="payment-result-card">
          <CardContent className="py-12 text-center space-y-6">
            {status === 'checking' && (
              <>
                <Loader2 size={64} className="mx-auto text-[#0052FF] animate-spin" />
                <CardTitle className="text-2xl">Processing Payment...</CardTitle>
                <p className="text-slate-500">Please wait while we verify your payment.</p>
              </>
            )}

            {status === 'success' && (
              <>
                <CheckCircle2 size={64} className="mx-auto text-green-500" />
                <CardTitle className="text-2xl text-green-700" data-testid="payment-success-title">Payment Successful!</CardTitle>
                <p className="text-slate-500">Your payment has been processed. Thank you for your order!</p>
                {paymentData && (
                  <p className="text-lg font-mono text-[#0052FF]">
                    &#8377;{(paymentData.amount_total / 100).toFixed(2)} {paymentData.currency?.toUpperCase()}
                  </p>
                )}
                <div className="flex gap-3 justify-center pt-4">
                  <Button onClick={() => navigate('/dashboard')} className="bg-[#0052FF] hover:bg-[#0043D1] rounded-sm" data-testid="back-to-dashboard-btn">
                    View Orders
                  </Button>
                </div>
              </>
            )}

            {(status === 'error' || status === 'expired' || status === 'timeout') && (
              <>
                <XCircle size={64} className="mx-auto text-red-500" />
                <CardTitle className="text-2xl text-red-700" data-testid="payment-failed-title">
                  {status === 'expired' ? 'Payment Expired' : 'Payment Issue'}
                </CardTitle>
                <p className="text-slate-500">
                  {status === 'expired'
                    ? 'Your payment session has expired. Please try again.'
                    : 'We could not verify your payment. Please check your email for confirmation.'}
                </p>
                <Button onClick={() => navigate('/dashboard')} className="bg-[#0052FF] hover:bg-[#0043D1] rounded-sm" data-testid="back-to-dashboard-btn">
                  Back to Dashboard
                </Button>
              </>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
